//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

(function () {
  angular.module('curalate').service('CollectionFiltersConverter', [
    'CONTENT_LIBRARY_SOURCES_GROUPS',
    'cuDateLiteService',
    'DateTimeService',
    function (
      CONTENT_LIBRARY_SOURCES_GROUPS,
      cuDateLiteService,
      DateTimeService,
    ) {
      const DATE_PICKER_DATE_SEPARATOR = '/';
      const processRightsStatus = function (item) {
        if (item === 'None') {
          return 'NotSet';
        }

        return item;
      };

      const processSemanticTag = (tag) => {
        return {
          ...tag,
          aliasKey: tag.tag,
        };
      };

      const addSocialFilterToInitialState = function (
        searchFilterSettings,
        filterType,
        filterValue,
      ) {
        const filterObj = {
          type: filterType,
          value: filterValue,
          aliasKey: filterValue,
        };
        searchFilterSettings.socialFilters.push(filterObj);
      };

      const hydrateDateRange = (dateString) => {
        const dateRangeObject = {};

        if (dateString) {
          const customDate = dateString.split(DATE_PICKER_DATE_SEPARATOR);

          // If the date string can be split, it's a custom date range.
          // Otherwise, it's a relative range.
          if (customDate.length === 2) {
            dateRangeObject.value = 'custom';
            dateRangeObject.alias = 'custom';
            dateRangeObject.startDate = DateTimeService.applyBrowserTimeZone(
              DateTimeService.userMoment(customDate[0], 'YYYY-MM-D'),
            ).toDate();
            dateRangeObject.endDate = DateTimeService.applyBrowserTimeZone(
              DateTimeService.userMoment(customDate[1], 'YYYY-MM-D'),
            ).toDate();
          } else {
            dateRangeObject.value = dateString;
            dateRangeObject.alias = dateString;
            dateRangeObject.aliasKey = dateString;
          }
        }
        return dateRangeObject;
      };

      const buildInitialFilterStateForContentFilters = function (filters) {
        const dateRange = _(filters).safeGet('dateRange');
        const visualScore = _(filters).safeGet('visualScore.min');
        const minHeight = _(filters).safeGet('height.min');
        const maxHeight = _(filters).safeGet('height.max');
        const minWidth = _(filters).safeGet('width.min');
        const maxWidth = _(filters).safeGet('width.max');
        const minAspectRatio = _(filters).safeGet('aspectRatio.min');
        const maxAspectRatio = _(filters).safeGet('aspectRatio.max');
        const excludeMatchedInstagramItemForInstagramStory = _(filters).safeGet(
          'duplicateItemExclusions.excludeMatchedInstagramItemForInstagramStory',
        );
        const excludeMatchedMisItemForInstagramPost = _(filters).safeGet(
          'duplicateItemExclusions.excludeMatchedMisItemForInstagramPost',
        );
        const excludeOriginalRepostedInstagramItem = _(filters).safeGet(
          'duplicateItemExclusions.excludeOriginalRepostedInstagramItem',
        );

        const searchFilterSettings = {
          excludedContentSources:
            _(filters).safeGet('excludedContentSources') || [],
          labels: _(filters).safeGet('labels.options') || [],
          mediaItemTypes: _(filters).safeGet('mediaTypes.options') || [],
          orientation: _(filters).safeGet('orientations.options') || [],
          hasProducts: _(filters).safeGet('productTags.options') || [],
          rightsStatuses: _.chain(filters)
            .safeGet('rights.options')
            .map(processRightsStatus)
            .value(),
          spamFiltering: _(filters).safeGet('spam') ? ['true'] : [],
          minHeight: minHeight ? [minHeight] : [],
          maxHeight: maxHeight ? [maxHeight] : [],
          minWidth: minWidth ? [minWidth] : [],
          maxWidth: maxWidth ? [maxWidth] : [],
          minAspectRatio: minAspectRatio ? [minAspectRatio] : [],
          maxAspectRatio: maxAspectRatio ? [maxAspectRatio] : [],
          excludeMatchedInstagramItemForInstagramStory:
            excludeMatchedInstagramItemForInstagramStory ? ['true'] : [],
          excludeMatchedMisItemForInstagramPost:
            excludeMatchedMisItemForInstagramPost ? ['true'] : [],
          excludeOriginalRepostedInstagramItem:
            excludeOriginalRepostedInstagramItem ? ['true'] : [],
          dateRange: dateRange ? [hydrateDateRange(dateRange)] : [],
          semanticTags:
            _.chain(filters)
              .safeGet('semanticTags.tags')
              .map(processSemanticTag)
              .value() || [],
          experimentTags: _(filters).safeGet('experimentTags') || {},
          visualScore: visualScore ? [visualScore] : [],
          visualSearchDetection:
            _(filters).safeGet('visualSearchDetection.detections') || [],
          socialFilters: [], // filled in below
          distribution: [], // filled in below
          isDistributed: [], // filled in below
        };

        if (!_.chain(filters).safeGet('distribution').isEmpty().value()) {
          searchFilterSettings.distribution = filters.distribution.options;
          searchFilterSettings.isDistributed = [
            filters.distribution.isDistributed.toString(),
          ];
        }

        _.chain(filters)
          .safeGet('instagramHashtags.options')
          .each((instagramHashtag) => {
            addSocialFilterToInitialState(
              searchFilterSettings,
              'hashtag',
              instagramHashtag,
            );
          });

        _.chain(filters)
          .safeGet('instagramUsers.options')
          .each((instagramUser) => {
            addSocialFilterToInitialState(
              searchFilterSettings,
              'username',
              instagramUser,
            );
          });

        return searchFilterSettings;
      };

      const buildInitialFilterStateForSourceCheckboxes = function (sources) {
        const searchSourceSettings = {
          sourcesGroups: [],
          brandFeedPost: sources.brandFeedPost ? ['true'] : [],
          brandStoryPost: sources.brandStoryPost ? ['true'] : [],
          brandTikTokPosts: sources.brandTikTokPosts,
          curalateUserIds: sources.curalateUsers,
          galleryUploaders: sources.galleryUploaders,
          shareCreatorClients: sources.shareCreatorClients,
          trackedBusinessUserIds: sources.instagramBusinessUsers,
          trackedCompetitorIds: sources.trackedCompetitorIds || [],
          trackedUserIds: sources.instagramUsers,
          trackedHashtags: sources.instagramHashtags,
          accountMentionIds: sources.instagramMentions,
          pinterestUserIds: sources.pinterestUsers,
          tiktokMentionedUserIds: sources.tiktokMentions,
        };

        if (sources.allMedia) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_MEDIA,
          );
        }
        if (sources.allBrandInstagram) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_BRAND_POSTS,
          );
        }
        if (sources.allCuralateUsers) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.TEAM_MEDIA,
          );
        }
        if (sources.allInstagramUsers) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_TRACKED_USERS,
          );
        }
        if (sources.allInstagramHashtags) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_TRACKED_HASHTAGS,
          );
        }
        if (sources.allInstagram) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_INSTAGRAM,
          );
        }
        if (sources.allPinterest) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_PINTEREST,
          );
        }
        if (sources.allDropbox) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_DROPBOX,
          );
        }
        if (sources.allTikTok) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_TIKTOK,
          );
        }
        if (sources.allYouTube) {
          searchSourceSettings.sourcesGroups.push(
            CONTENT_LIBRARY_SOURCES_GROUPS.ALL_YOUTUBE,
          );
        }

        return searchSourceSettings;
      };

      this.parseDatePickerFilterObject = (datePickerObj) => {
        let dateRangeString = '';
        const dateAlias = _(datePickerObj).safeGet('alias');
        const dateSeparator = '/';

        switch (dateAlias) {
          case 'custom':
            dateRangeString = DateTimeService.buildDateRangeParam(
              {
                from: moment(datePickerObj.startDate),
                to: moment(datePickerObj.endDate),
              },
              dateSeparator,
            );
            break;
          case cuDateLiteService.ALIASES.last24Hours.name:
          case cuDateLiteService.ALIASES.last7Days.name:
          case cuDateLiteService.ALIASES.last30Days.name:
          case cuDateLiteService.ALIASES.last3Months.name:
            dateRangeString = dateAlias;
            break;
          case cuDateLiteService.ALIASES.allTime.name:
          default:
            return [];
        }

        return dateRangeString;
      };

      this.collectionToFilters = (collection) => {
        const filtersFilters = buildInitialFilterStateForContentFilters(
          collection.filters,
        );
        const sourcesFilters = buildInitialFilterStateForSourceCheckboxes(
          collection.sources,
        );
        const orderByKey = _(collection).safeGet('sort.key');
        return {
          filtersFilters,
          sourcesFilters,
          orderByKey,
        };
      };
    },
  ]);
})();
